import {AttrsSchema, Component, ConfigOptions} from 'panel';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import type {Point, SeriesLineOptions} from 'highcharts';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {commaizeNumber, htmlEncodeString} from 'common/util';
import {ChartType, REPORT_LIMITS, ReportName, SavedLegend, ValueRepresentationType} from 'common/report/constants';
import type {BarChartPopover} from 'common/report/funnels/visualization/chart-display';
import {getOverallStepsFromFunnelSegment} from 'common/report/funnels/util';
import {DisplayAnalysisType, LineStyle} from 'common/charts/visualization/line-chart/enums';
import {
  formatSumOfAllBucketDatas,
  formatXAxis,
  formatYAxisLabel,
  getBucketData,
  getFunnelStep,
  getLineChartDataforBucketReports,
  getStepFromFunnelData,
  getToolTip,
  getTooltipDataFromSegments,
  percentOfTotal,
} from 'common/report/funnels/models/bar-chart/utils';
import {
  conversionMode,
  FunnelStepBucketAccessors,
  getBucketLabel,
  getLiftToolTipText,
  getToolTipText,
  partialFormatter,
} from 'common/report/funnels/models/frequency-chart/utils';
import {BucketDefinition, SelectedSegmentStep} from 'common/report/funnels/models/bar-chart/types';
import {ColorChoice, FunnelBookmark, FunnelSegment, FunnelStep} from 'common/report/funnels/models/types';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';
import {createBucketLineChartFunnelData, getCalendarDateRangeLabels} from 'common/report/funnels/models/utils';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {getMedianAccessor} from 'common/report/funnels/models/time-to-convert/utils';
import {processFunnelForChartWidget} from 'common/report/funnels/models/aggregates';
import {LineChartAttrs} from 'common/charts/visualization/line-chart';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';
import {bookmarkHasLiftComparison} from 'common/report/funnels/util/bar-chart';
import {LiftType} from 'common/report/util/lift/types';
import {TooltipSection} from 'common/components/chart-tooltip/types';
import {OVERALL_PROP} from 'common/report/funnels/models/constants';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';

import {template} from './template';
import css from './index.inline.styl';

interface ChartState {
  funnelData: Map<string, SelectedSegmentStep>;
  comparisonData: Map<string, SelectedSegmentStep>;
  liftData: Map<string, SelectedSegmentStep>;
  segmentData: FunnelSegment[];
  comparisonSegmentData: FunnelSegment[];
  liftSegmentData: FunnelSegment[];
  funnelQuery: FunnelsQuery;
}

interface ChartAttrs {
  'include-header': boolean;
  'enable-point-click'?: boolean;
}

export class FunnelsFrequencyChart extends Component<ChartState, ChartAttrs, any, any, ChartsContextRegistry> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;
  private _projectDetails: any;

  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      ...super.attrsSchema,
      'include-header': `boolean`,
      'enable-point-click': `boolean`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.processData();
  }

  resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => !this._projectDetails && this.processData()]]);

  get config(): ConfigOptions<ChartState, any, ChartsContextRegistry> {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      defaultState: {
        funnelData: null,
        comparisonData: null,
        segmentData: [],
        liftData: null,
        liftSegmentData: [],
        comparisonSegmentData: [],
        funnelQuery: null,
      },
      helpers: {
        ValueRepresentationType,
        isHidden: () => false,
        isConversionRate: () => this.isConversionRate(),
        hasData: () => {
          const stepData = this.state.segmentData?.[0]?.steps;
          if (!stepData || stepData.length === 0) {
            return false;
          }
          return this.conversionMode().bucketAccessor in stepData[0];
        },
        subtitleText: () => {
          const {bucketAccessor, representationText} = this.conversionMode();
          const funnelStep = getFunnelStep(this.representativeSteps, this.stepIndex);
          if (!funnelStep) {
            return null;
          }
          const bucketData = getBucketData(funnelStep, bucketAccessor);
          const overallConversionRatio = this.representativeSteps[this.representativeSteps.length - 1]
            .overall_conv_ratio;
          const conversionNumber = this.isConversionRate()
            ? (overallConversionRatio * 100).toFixed(2) + `%`
            : formatSumOfAllBucketDatas(bucketData);

          return {
            conversionText: `${conversionNumber} ${representationText}`,
            totalInStepText: `${commaizeNumber(funnelStep.count)} total`,
          };
        },

        isSegmented: () => this.state.segmentData?.length > 1,

        lineChartData: () => {
          const segmentColors = this.getSelectedSegmentColors();
          const {bucketAccessor} = this.conversionMode();
          if (!isEmpty(this._bookmark.liftComparison)) {
            return getLineChartDataforBucketReports({
              segmentData: this.state.liftSegmentData,
              stepIndex: this.stepIndex,
              bucketAccessor: bucketAccessor as keyof FunnelStep,
              segmentColors,
              isComparison: false,
              representationType: this.valueRepresentation,
              showCountAsPercentage: this.isConversionRate(),
            });
          }
          let chartData = getLineChartDataforBucketReports({
            segmentData: this.state.segmentData,
            stepIndex: this.stepIndex,
            bucketAccessor: bucketAccessor as keyof FunnelStep,
            segmentColors,
            isComparison: false,
            representationType: this.valueRepresentation,
            showCountAsPercentage: this.isConversionRate(),
          });
          if (this.hasComparison()) {
            chartData = [
              ...chartData,
              ...getLineChartDataforBucketReports({
                segmentData: this.state.comparisonSegmentData,
                stepIndex: this.stepIndex,
                bucketAccessor: bucketAccessor as keyof FunnelStep,
                segmentColors,
                isComparison: true,
                representationType: this.valueRepresentation,
                showCountAsPercentage: this.isConversionRate(),
              }),
            ];
          }
          return chartData;
        },
        lineChartClick: (point: Point) => {
          const {series, index, color, plotX: clientX, plotY: clientY} = point;
          if ((series.options as SeriesLineOptions).dashStyle === LineStyle.Comparison) {
            return;
          }
          const {name: segmentKey} = series;
          const {step: funnelStep, segmentName} = this.state.funnelData.get(segmentKey);
          const bucketData = getBucketData(funnelStep, this.conversionMode().bucketAccessor);
          const buckets = bucketData.buckets;
          const count = buckets[index];

          const selection: BarChartPopover = {
            segment: {
              color: color as string,
              count,
              // @ts-ignore (maybe we don't need this property) Object literal may only specify known properties
              percentage: percentOfTotal(bucketData, count),
              isSelected: true,
              isClickable: true,
              label: segmentName,
              name: segmentKey,
            },
            rowLabel: formatXAxis(index, this.frequencyBuckets, getBucketLabel, true),
            queryIndex: index,
            clientX,
            clientY,
            point,
          };

          this.dispatchEvent(
            new CustomEvent(`openPopover`, {
              detail: {
                selection,
              },
            }),
          );
        },
        getLineChartAttrs: (): Partial<LineChartAttrs> => {
          const projectDetails = this.getProjectDetails();
          return {
            'chart-width': null,
            'display-analysis-type': DisplayAnalysisType.Linear,
            timezone: projectDetails.projectTimeZone,
            'utc-offset': projectDetails.utcOffset,
            'zoom-type': `x`,
            ...(this.valueRepresentation === ValueRepresentationType.Relative
              ? {'y-axis-options': {maxPadding: 0}}
              : {}),
          };
        },

        getLineChartProps: () => {
          return {
            chartData: this.helpers.lineChartData(),
            formatTooltip: this.helpers.formatTooltip,
            formatXAxisLabel: this.helpers.formatXAxisLabel,
            formatYAxisLabel: this.helpers.formatYAxisLabel,
            onHighChartClick: this.attr(`enable-point-click`) && this.helpers.lineChartClick,
          };
        },
        formatTooltip: (x: number, _y: number, series: any, _idx: number) => {
          const {color, name: seriesName} = series;

          const countBucketAccessor = this.isConversionRate() ? FunnelStepBucketAccessors.ConvertStepFreqBuckets : null;
          const {bucketAccessor, medianAccessor} = this.conversionMode();
          const dateRanges = this.getDateRangeLabel();

          const labelFormatter = {
            ...partialFormatter,
            getToolTipText: (count, percentage, bucketLabel) =>
              getToolTipText(
                count,
                percentage,
                bucketLabel,
                !(x === 1 && this.frequencyBuckets.bucketSize === 1),
                this.representativeSteps?.length > 0
                  ? getFunnelStep(this.representativeSteps, this.stepIndex).step_label
                  : getStepFromFunnelData(this.state.funnelData, seriesName).step_label,
                this._bookmark.frequencyType,
              ),
          };

          let sections: TooltipSection[];

          if (bookmarkHasLiftComparison(this._bookmark)) {
            let referenceSegmentName;
            let referenceSegment;

            switch (this._bookmark.liftComparison.type) {
              case LiftType.Time:
                referenceSegmentName = seriesName;
                referenceSegment = this.state.comparisonData;
                break;
              case LiftType.Segment:
                referenceSegmentName = this._bookmark.liftComparison.groupValue;
                referenceSegment = this.state.funnelData;
                break;
              case LiftType.Overall:
                // TODO: createFunnelDataWithRename() removes the $overall key from the segments when processing; we'll have to add configuration
                // or something to enable this to work (segment is missing atm)
                referenceSegmentName = OVERALL_PROP;
                referenceSegment = this.state.funnelData;
                break;
            }

            const bucketDataFromLiftComparisonSegment = getTooltipDataFromSegments(
              referenceSegment,
              referenceSegmentName,
              bucketAccessor,
              labelFormatter,
              medianAccessor,
              x,
              countBucketAccessor,
            );

            const bucketDataFromOriginalSegment = getTooltipDataFromSegments(
              this.state.funnelData,
              seriesName,
              bucketAccessor,
              labelFormatter,
              medianAccessor,
              x,
              countBucketAccessor,
            );
            const bucketDataFromLiftSegment = getTooltipDataFromSegments(
              this.state.liftData,
              seriesName,
              bucketAccessor,
              labelFormatter,
              medianAccessor,
              x,
              countBucketAccessor,
            );

            sections = [
              {
                rows: getLiftToolTipText({
                  count: bucketDataFromOriginalSegment.count,
                  liftComparisonCount: bucketDataFromLiftComparisonSegment.count,
                  segmentName: seriesName,
                  percentage: bucketDataFromLiftSegment.percentage * 100,
                  bucketLabel: formatXAxis(x, this.frequencyBuckets, getBucketLabel, true),
                  baselineSegmentName: referenceSegmentName,
                  plural: x > 1, // todo
                  stepName: seriesName,
                  liftType: this._bookmark.liftComparison.type,
                  dateRanges,
                }),
              },
            ];
          } else {
            sections = getToolTip(
              formatXAxis(x, this.frequencyBuckets, getBucketLabel, true),
              seriesName,
              x,
              false,
              labelFormatter,
              this.state.funnelData,
              this.state.comparisonData,
              bucketAccessor,
              medianAccessor,
              dateRanges,
              countBucketAccessor,
              this.isConversionRate(),
              color,
            );
          }

          let titleColor = ``;

          if (!this.hasComparison() || bookmarkHasLiftComparison(this._bookmark)) {
            titleColor = `title-color=${color}`;
          }
          const toolTipAttrs = `${titleColor} sections="${htmlEncodeString(JSON.stringify(sections))}"`;

          return `
                <mp-chart-tooltip ${toolTipAttrs}>
              `;
        },
        formatXAxisLabel: (value: number) => {
          return formatXAxis(value, this.frequencyBuckets, getBucketLabel);
        },
        formatYAxisLabel: (value: number) => {
          return formatYAxisLabel(value, this.valueRepresentation);
        },
      },
    };
  }

  hasComparison() {
    return this.state.comparisonSegmentData?.length > 0;
  }

  get stepIndex() {
    return this._bookmark.eventIndex || 0;
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public get funnelsResponse(): FunnelQueryResponse {
    return this._funnelsResponse;
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  public get bookmark(): FunnelBookmark {
    return this._bookmark;
  }

  public set projectDetails(value) {
    if (!isEqual(this._projectDetails, value)) {
      this._projectDetails = value;
      this.processData();
    }
  }

  processData() {
    if (!this._bookmark || !this._funnelsResponse || !this.isConnected) {
      return;
    }

    const stepIndex = this.stepIndex;

    const projectDetails = this.getProjectDetails();

    const {legendSegmentItems, savedLegend, stateUpdates} = processFunnelForChartWidget(
      this._bookmark,
      this.funnelsResponse,
      projectDetails.projectId,
      projectDetails.projectAsOfDate,
      stepIndex,
      getMedianAccessor(stepIndex),
      projectDetails.projectTimeZone,
    );

    const {query: funnelQuery, formattedData, formattedCompareData, formattedLiftData} = stateUpdates;

    this.update({funnelQuery});
    this.updateSegmentFilterController(legendSegmentItems, savedLegend);
    this.updateFunnelChartData(formattedData, formattedCompareData, formattedLiftData);
  }

  updateFunnelChartData(
    formattedData: FunnelSegment[],
    formattedCompareData: FunnelSegment[],
    formattedLiftData: FunnelSegment[],
  ) {
    const selected = this.getSelectedSegmentColors();

    const mappedFunnelData = createBucketLineChartFunnelData({
      segmentData: formattedData,
      selected,
      stepIndex: this.stepIndex,
      hasComparison: this.hasComparison(),
      isComparisonData: false,
    });

    const mappedComparisonData = formattedCompareData
      ? createBucketLineChartFunnelData({
          segmentData: formattedCompareData,
          selected,
          stepIndex: this.stepIndex,
          hasComparison: this.hasComparison(),
          isComparisonData: true,
        })
      : null;

    const mappedLiftData = formattedLiftData
      ? createBucketLineChartFunnelData({
          segmentData: formattedLiftData,
          selected,
          stepIndex: this.stepIndex,
          hasComparison: false,
          isComparisonData: false,
        })
      : null;

    this.update({
      funnelData: mappedFunnelData,
      comparisonData: mappedComparisonData,
      liftData: mappedLiftData,
      segmentData: formattedData,
      comparisonSegmentData: formattedCompareData,
      liftSegmentData: formattedLiftData,
    });
  }

  private getProjectDetails() {
    if (this._projectDetails) {
      return this._projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject) {
      return {
        projectTimeZone: currentProject.timezone,
        projectAsOfDate: currentProject.projectAsOfDate,
        utcOffset: currentProject.utcOffset,
        projectId: currentProject.id.toString(),
      };
    }
    return null;
  }

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  private updateSegmentFilterController(legendSegmentItems: SegmentItem[], savedLegend: SavedLegend[]) {
    this.segmentFilterController.loadSections([{items: legendSegmentItems}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelFrequencyLine].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelFrequencyLine].max,
    });
    this.segmentFilterController.loadFromSavedLegend(savedLegend);
  }

  conversionMode() {
    return conversionMode(this._bookmark.frequencyType);
  }

  getDateRangeLabel() {
    const {projectAsOfDate, projectTimeZone} = this.getProjectDetails();
    return getCalendarDateRangeLabels(this._bookmark, this.funnelsResponse, projectAsOfDate, projectTimeZone);
  }

  get frequencyBuckets(): BucketDefinition {
    const frequencyBuckets = this._bookmark.frequency_buckets;
    return {
      min: frequencyBuckets.min,
      max: frequencyBuckets.max,
      offset: frequencyBuckets.offset,
      bucketSize: frequencyBuckets.bucket_size,
    };
  }

  get valueRepresentation() {
    return this.isConversionRate() ? ValueRepresentationType.Relative : this._bookmark.frequencyValueRepresentation;
  }

  get representativeSteps() {
    return !this.state.funnelQuery?.isGroupedSteps() ? getOverallStepsFromFunnelSegment(this.state.segmentData) : [];
  }

  getSelectedSegmentColors(): ColorChoice[] {
    return this.segmentFilterController
      .getSelectedItems(0)
      .map((seg) => ({key: seg.id, color: this.segmentFilterController.getItemColor(0, seg.id)}));
  }

  isConversion(): boolean {
    return this._bookmark.frequencyType === FrequencyViewType.Conversion;
  }

  isConversionRate(): boolean {
    return this._bookmark.frequencyType === FrequencyViewType.ConversionRate;
  }
}

export const FunnelsFrequencyLineChartEl = defineAndMakeJsxFactory(
  `mp-funnel-frequency-line-chart`,
  FunnelsFrequencyChart,
);
export const FunnelsFrequencyLineChartReact = panel2react(`mp-funnel-frequency-line-chart`, FunnelsFrequencyChart);
